import { api } from '@/lib/api';
import { build_params, download_export } from '@/lib/list-query';
import type { SortState } from '@/hooks/use-url-state';
import type {
  CatalogCounts,
  CatalogListResponse,
  CatalogProduct,
  CatalogQuery,
  CreateCatalogInput,
  UpdateCatalogInput,
} from './types';

export async function fetch_catalog_products(
  query: CatalogQuery,
  sort?: SortState,
): Promise<CatalogListResponse> {
  const res = await api.get<CatalogListResponse>('/admin/catalog-products', {
    params: build_params(query as Record<string, never>, sort),
  });
  return res.data;
}

/** The tiles. Describes the whole catalogue, so it deliberately ignores the filters. */
export async function fetch_catalog_counts(): Promise<CatalogCounts> {
  const res = await api.get<CatalogCounts>('/admin/catalog-products/counts');
  return res.data;
}

export async function export_catalog_products(
  query: CatalogQuery,
  sort?: SortState,
): Promise<void> {
  await download_export(
    '/admin/catalog-products/export',
    build_params(query as Record<string, never>, sort),
    'catalogue.csv',
  );
}

/** One master, for its own page. */
export async function fetch_catalog_product(id: string): Promise<CatalogProduct> {
  const res = await api.get<CatalogProduct>(`/admin/catalog-products/${id}`);
  return res.data;
}

export async function create_catalog_product(input: CreateCatalogInput): Promise<CatalogProduct> {
  const res = await api.post<CatalogProduct>('/admin/catalog-products', input);
  return res.data;
}

export async function update_catalog_product(id: string, input: UpdateCatalogInput): Promise<CatalogProduct> {
  const res = await api.patch<CatalogProduct>(`/admin/catalog-products/${id}`, input);
  return res.data;
}

/** How many vendor listings are linked — an edit reaches all of them. */
export async function fetch_catalog_vendor_count(id: string): Promise<number> {
  const res = await api.get<{ vendor_count: number }>(`/admin/catalog-products/${id}/vendors`);
  return res.data.vendor_count;
}

/**
 * Append a photo to the gallery.
 *
 * Appends rather than replaces: the first photo is the one every vendor listing and
 * search result shows, and a new upload should not silently take that slot.
 */
export async function add_catalog_image(id: string, file: File, source_note?: string): Promise<CatalogProduct> {
  const form = new FormData();
  form.append('file', file);
  if (source_note) form.append('source_note', source_note);
  const res = await api.post<CatalogProduct>(`/admin/catalog-products/${id}/images`, form);
  return res.data;
}

export async function delete_catalog_image(id: string, image_id: string): Promise<CatalogProduct> {
  const res = await api.delete<CatalogProduct>(`/admin/catalog-products/${id}/images/${image_id}`);
  return res.data;
}

/** Set the whole order. The API wants every id exactly once. */
export async function reorder_catalog_images(id: string, image_ids: string[]): Promise<CatalogProduct> {
  const res = await api.put<CatalogProduct>(`/admin/catalog-products/${id}/images/order`, { image_ids });
  return res.data;
}

/**
 * Set or clear the fee for one curated product.
 *
 * Wider reach than a per-listing override: set here it applies to every vendor selling
 * this product, so a well-known item is priced once instead of once per vendor. A null
 * mode clears it and the product inherits its category again.
 */
export async function set_catalog_commission(
  id: string,
  input: { mode: 'RATE' | 'FLAT' | null; rate_pct?: number | null; amount_inr?: number | null },
): Promise<CatalogProduct> {
  const res = await api.put<CatalogProduct>(`/admin/catalog-products/${id}/commission`, input);
  return res.data;
}
